import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, ArchiveRestore } from "lucide-react";
import { useAppStore } from "lib/store/useAppStore";
import { getProject } from "lib/api/getProject";
import { getProjectNodes, getProjectDeletedFiles, postRestoreProjectFiles } from "lib/api/projectStructure";
import ProjectTreeManager from "components/project/ProjectTreeEditor";
import "css/projectSettings.css";

function DeletedProjectFiles({ projectId, onRestored }) {
  const [data, setData] = useState({ files: [], total: 0, limit: 30 });
  const [page, setPage] = useState(1);
  const [version, setVersion] = useState(0);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setSelected([]);
    getProjectDeletedFiles(projectId, page, controller.signal).then(result => {
      if (controller.signal.aborted) return;
      if (result.success) {
        const lastPage = Math.max(1, Math.ceil(result.data.total / result.data.limit));
        if (page > lastPage) { setPage(lastPage); return; }
        setData(result.data);
      } else { setData({ files: [], total: 0, limit: 30 }); setMessage(result.message); }
    }).catch(error => {
      if (error.name !== "AbortError") setMessage("삭제 파일을 불러오지 못했습니다.");
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [projectId, page, version]);

  const restore = async () => {
    if (!selected.length || restoring) return;
    setRestoring(true);
    setMessage("");
    try {
      const result = await postRestoreProjectFiles(projectId, selected);
      setMessage(result.message);
      if (result.success) {
        setSelected([]);
        setVersion(value => value + 1);
        await onRestored();
      }
    } finally { setRestoring(false); }
  };
  const busy = loading || restoring;
  return <section className="projectSettings__section" aria-labelledby="deletedFilesTitle">
    <div className="projectSettings__sectionTitle">
      <div className="projectSettings__sectionLead"><ArchiveRestore aria-hidden="true" /><div>
        <h3 id="deletedFilesTitle">삭제된 파일</h3><p>프로젝트에서 제외된 파일을 확인하고 다시 복원합니다.</p>
      </div></div><span>총 {data.total}개</span>
    </div>
    <div className="projectSettings__actions">
      <p className="projectSettings__restoreHint">복원한 파일은 기존 경로와 파일 정보를 유지합니다.</p>
      <div className="projectSettings__actionButtons">
      <button type="button" className="projectSettings__secondary" disabled={busy} onClick={() => { setMessage(""); setVersion(value => value + 1); }}>목록 새로고침</button>
      <button type="button" className="btn-bg-blue" disabled={busy || !selected.length} onClick={restore}>
        {restoring ? "복원 중…" : `선택 파일 복원 (${selected.length})`}
      </button>
      </div>
    </div>
    {message && <p role="status" className="projectSettings__message">{message}</p>}
    <div className="projectSettings__deletedWrap" aria-busy={loading}>
      <table className="projectSettings__deletedTable">
        <thead><tr>
          <th><input type="checkbox" aria-label="현재 페이지 삭제 파일 전체 선택" disabled={busy || !data.files.length}
            checked={data.files.length > 0 && selected.length === data.files.length}
            onChange={event => setSelected(event.target.checked ? data.files.map(file => file.upload_id) : [])} /></th>
          <th>파일명</th><th>업로드 날짜</th><th>건물명</th><th>층</th><th>위치</th><th>설비명</th>
        </tr></thead>
        <tbody>{loading ? <tr><td colSpan={7}>불러오는 중…</td></tr> : data.files.length ? data.files.map(file => <tr key={file.upload_id} className={selected.includes(file.upload_id) ? "is-selected" : ""}>
          <td><input type="checkbox" aria-label={`${file.file_name} 복원 선택`} disabled={busy} checked={selected.includes(file.upload_id)}
            onChange={event => setSelected(current => event.target.checked ? [...current, file.upload_id] : current.filter(id => id !== file.upload_id))} /></td>
          <td>{file.file_name}</td><td>{String(file.uploaded_at ?? "").replace("T", " ").slice(0, 16) || "-"}</td>
          <td>{file.building_name || "미지정"}</td><td>{file.floor || "미지정"}</td><td>{file.location || "미지정"}</td><td>{file.facility_name || "-"}</td>
        </tr>) : <tr><td colSpan={7}>삭제된 파일이 없습니다.</td></tr>}</tbody>
      </table>
    </div>
    <div className="projectSettings__pagination">
      <button className="projectSettings__secondary" disabled={busy || page <= 1} onClick={() => setPage(value => value - 1)}>이전</button>
      <span>{page} / {Math.max(1, Math.ceil(data.total / data.limit))}</span>
      <button className="projectSettings__secondary" disabled={busy || page * data.limit >= data.total} onClick={() => setPage(value => value + 1)}>다음</button>
    </div>
  </section>;
}

export default function ProjectSettingsPage() {
  const { project_id: projectId } = useParams();
  const location = useLocation();
  const user = useAppStore(state => state.user);
  const fetchProjectTree = useAppStore(state => state.fetchProjectTree);
  const setLastSelectedProject = useAppStore(state => state.setLastSelectedProject);
  const [project, setProject] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setProject(null);
    setNodes([]);
    setMessage("");
    Promise.all([getProject(projectId), getProjectNodes(projectId, controller.signal)]).then(([record, tree]) => {
      if (controller.signal.aborted) return;
      if (!record.success || !record.project) { setMessage(record.message); return; }
      setProject(record.project);
      if (tree.success) setNodes(tree.data.nodes);
      else setMessage(tree.message);
      setLastSelectedProject(projectId, record.project.project_name);
    }).catch(error => { if (error.name !== "AbortError") setMessage("프로젝트 설정을 불러오지 못했습니다."); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [projectId, setLastSelectedProject]);

  const refreshTree = useCallback(async nextNodes => {
    if (Array.isArray(nextNodes)) setNodes(nextNodes);
    else {
      const result = await getProjectNodes(projectId);
      if (result.success) setNodes(result.data.nodes);
    }
    await fetchProjectTree(user?.id, projectId);
  }, [fetchProjectTree, user?.id, projectId]);
  return <section className="content projectSettingsPage">
    <div className="projectSettings__heading"><div>
      <h2>프로젝트 설정</h2>
      <p>현장 경로와 대표 이미지, 삭제된 파일을 관리합니다. 보고서 기재 정보는 <Link to={`/project/${projectId}/report`}>보고서 메뉴</Link>에서 수정하세요.</p>
    </div><Link className="projectSettings__secondary" to={`/project/${projectId}${location.search}`}><ArrowLeft size={16} aria-hidden="true" /> 파일 목록으로</Link></div>
    {message && <p role="status" className="projectSettings__message">{message}</p>}
    {loading ? <p role="status">프로젝트 설정을 불러오는 중…</p> : project && <>
      <div className="projectSettings__section"><ProjectTreeManager key={projectId} projectId={projectId} projectName={project.project_name} nodes={nodes} onRegistered={refreshTree} /></div>
      <DeletedProjectFiles key={projectId} projectId={projectId} onRestored={refreshTree} />
    </>}
  </section>;
}
